import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Grid, IconButton } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useMakeSortedSegmentFields } from "../hooks/use-sorted-segment-fields";
import { ConditionField } from "./condition-field";
import { OperationField } from "./operation-field";
import { SegmentValueField } from "./value-field";

import type { NoNullFields } from "../../../../../server/supabase/base-supabase-service";
import type { UsableAccountPersonField } from "../../../server/supabase-segment-field-service";
import type { Condition, Segment } from "../../../types/segment-schema";
import type { SegmentManagerAction, SegmentReducerState } from "../reducer/actions";
import type { Dispatch, FC } from "react";

export const ConditionRow: FC<{
  groupIndex: number;
  conditionIndex: number;
  condition: Condition;
  dispatch: Dispatch<SegmentManagerAction<Segment>>;
  segmentFields: NoNullFields<UsableAccountPersonField>[];
  errors: SegmentReducerState<Segment>["errors"];
  allowDelete: boolean;
  disabled: boolean;
  recipientTypeKey?: string;
}> = ({
  groupIndex,
  conditionIndex,
  condition,
  dispatch,
  segmentFields,
  errors,
  allowDelete,
  disabled,
  recipientTypeKey,
}) => {
  const makeSortedSegmentFields = useMakeSortedSegmentFields(segmentFields);

  // Kept stable between renders: the Field box resets what's being typed whenever its value
  // changes identity, and the row re-renders whenever the shared segment document updates.
  const { fields, groupingFn, currentField } = useMemo(
    () => makeSortedSegmentFields(condition.field),
    [makeSortedSegmentFields, condition.field],
  );

  const handleUpdate = useCallback(
    (updates: Partial<Condition>) => {
      dispatch({
        type: "UPDATE_CONDITION",
        payload: {
          groupIndex,
          conditionIndex,
          condition: { ...condition, ...updates },
        },
      });
    },
    [dispatch, groupIndex, conditionIndex, condition],
  );

  const handleDelete = useCallback(() => {
    dispatch({
      type: "REMOVE_CONDITION",
      payload: { groupIndex, conditionIndex },
    });
  }, [dispatch, groupIndex, conditionIndex]);

  const error = errors.display
    ? errors.errors?.properties?.segment_json?.properties?.segments?.items?.[groupIndex]?.properties
        ?.conditions?.items?.[conditionIndex]
    : undefined;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        width: "100%",
        border: { sm: "1px dotted #ccc", md: "none" },
        borderRadius: { sm: 1, md: 0 },
        p: { sm: 1, md: 0 },
      }}
      data-testid="segment-condition"
    >
      <Grid size={{ sm: 12, md: 4 }}>
        <ConditionField
          segmentFields={fields}
          groupingFn={groupingFn}
          condition={condition}
          handleFieldChange={handleUpdate}
          disabled={disabled}
          errMsg={error?.properties?.field?.errors?.join(", ")}
        />
      </Grid>

      <Grid size={{ sm: 12, md: 4 }}>
        <OperationField
          condition={condition}
          handleFieldChange={handleUpdate}
          disabled={disabled}
          field={currentField}
          segmentFields={fields}
          errMsg={error?.properties?.operation?.errors?.join(", ")}
        />
      </Grid>

      <Grid size={{ sm: 12, md: 3 }}>
        <SegmentValueField
          condition={condition}
          handleFieldChange={handleUpdate}
          errMsg={error?.properties?.values?.errors?.join(", ")}
          disabled={disabled}
          fieldType={currentField.field.field_type}
          segmentField={currentField.field}
          recipientTypeKey={recipientTypeKey}
        />
      </Grid>

      <Grid
        size={{ sm: 12, md: 1 }}
        sx={{ justifyContent: "left", display: "flex", alignItems: "center" }}
      >
        {allowDelete && (
          <IconButton
            aria-label="delete-condition"
            onClick={handleDelete}
            data-analytics-id="delete-segment-condition"
            disabled={disabled}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};
